/**
 * Suche der Befehlspalette (Strg+K): Server, Seiten, Abschnitte („wartung" → System ›
 * Wartungsmodus) und Server-Unterpunkte („atm10 ram" → ATM 10 › Arbeitsspeicher),
 * dazu Tab-Vervollständigung wie bei einer Suchmaschine.
 * Reine Logik ohne React – getestet in tests/api/palette-search.test.ts.
 */

export type PaletteIcon =
  | 'plus' | 'server' | 'stats' | 'storage' | 'funding' | 'users' | 'audit' | 'system' | 'settings'
  | 'maintenance' | 'network' | 'ram' | 'discord' | 'cleanup' | 'files' | 'power' | 'monitor'
  | 'overview' | 'console' | 'logs' | 'software' | 'modpacks' | 'optimize' | 'players' | 'map'
  | 'announcements' | 'backups' | 'general' | 'game' | 'java' | 'updates' | 'danger' | 'account' | 'ui' | 'logins';

export interface PaletteItem {
  id: string;
  kind: 'action' | 'server' | 'page' | 'section' | 'server-tab' | 'server-section';
  label: string;
  /** Zweite Zeile, z. B. „System" oder „ATM 10 · Einstellungen". */
  sub?: string;
  keywords?: string;
  icon: PaletteIcon;
  /** Ziel-Route (HashRouter-Pfad). */
  to?: string;
  /** Element-ID, zu der nach dem Seitenwechsel gescrollt wird. */
  anchor?: string;
  action?: 'create-server' | 'system-monitor';
  serverId?: string;
  serverName?: string;
}

export interface PaletteServer { id: string; name: string; type?: string; status?: string; version?: string }

type Can = (perm: string) => boolean;
type Kind = 'java' | 'bedrock' | 'steam';

/** Kleinbuchstaben, ohne Umlaut-Punkte und ß → ss („Wächter" findet man mit „wachter"). */
export const normalize = (s: string) =>
  s.toLowerCase().replace(/ß/g, 'ss').normalize('NFD').replace(/[̀-ͯ]/g, '');
const compact = (s: string) => normalize(s).replace(/[^a-z0-9]/g, '');
const words = (s: string) => normalize(s).split(/[^a-z0-9]+/).filter(Boolean);

const kindOf = (type?: string): Kind => {
  const t = String(type || '').toLowerCase();
  if (t.startsWith('steam')) return 'steam';
  if (t === 'bedrock' || t === 'minecraft-bedrock') return 'bedrock';
  return 'java';
};

const PAGES: { p: string; label: string; icon: PaletteIcon; keywords: string; allowed: (can: Can) => boolean }[] = [
  { p: 'stats', label: 'Statistik', icon: 'stats', keywords: 'spielzeit spieler aktivitat', allowed: (c) => c('can_view_player_stats') },
  { p: 'storage', label: 'Speicher & Archiv', icon: 'storage', keywords: 'platz festplatte hdd ssd drive archiv', allowed: (c) => c('can_manage_storage') },
  { p: 'funding', label: 'Server-Kasse', icon: 'funding', keywords: 'kasse geld beitrag spende', allowed: () => true },
  { p: 'users', label: 'Benutzer', icon: 'users', keywords: 'nutzer rechte rollen accounts', allowed: (c) => c('can_manage_users') },
  { p: 'audit', label: 'Audit-Log', icon: 'audit', keywords: 'protokoll verlauf wer hat', allowed: (c) => c('can_view_audit_logs') },
  { p: 'system', label: 'System', icon: 'system', keywords: 'panel rechner host', allowed: (c) => c('can_manage_system') || c('can_root_reboot') || c('can_manage_maintenance') },
  { p: 'settings', label: 'Einstellungen', icon: 'settings', keywords: 'profil konto', allowed: () => true },
  { p: 'bugs', label: 'Fehler melden', icon: 'audit', keywords: 'bug problem melden', allowed: () => true },
];

const SYSTEM_SECTIONS: { id: string; label: string; icon: PaletteIcon; keywords: string; allowed: (can: Can) => boolean }[] = [
  { id: 'sys-wartung', label: 'Wartungsmodus – alle Server', icon: 'maintenance', keywords: 'wartung maintenance sperren countdown', allowed: (c) => c('can_manage_maintenance') || c('can_manage_system') || c('can_root_reboot') },
  { id: 'sys-netz', label: 'Netzwerk-Limits', icon: 'network', keywords: 'netzwerk bandbreite upload download hochladen herunterladen limit', allowed: (c) => c('can_manage_system') },
  { id: 'sys-ram', label: 'RAM-Wächter', icon: 'ram', keywords: 'ram watchdog arbeitsspeicher puffer', allowed: (c) => c('can_manage_system') },
  { id: 'sys-discord', label: 'Discord-Benachrichtigungen', icon: 'discord', keywords: 'discord webhook benachrichtigung meldung', allowed: (c) => c('can_manage_system') },
  { id: 'sys-aufraeumen', label: 'Aufräumen', icon: 'cleanup', keywords: 'bereinigen temporar cleanup cache logs platz', allowed: (c) => c('can_manage_system') || c('can_root_reboot') || c('can_manage_maintenance') },
  { id: 'sys-dateien', label: 'Systemdateien', icon: 'files', keywords: 'dateien dateimanager panel config', allowed: (c) => c('can_root_reboot') },
  { id: 'sys-gefahr', label: 'Rechner neu starten / ausschalten', icon: 'power', keywords: 'reboot neustart ausschalten herunterfahren panel rechner host', allowed: (c) => c('can_root_reboot') },
];

const ME_SECTIONS: { id: string; label: string; icon: PaletteIcon; keywords: string }[] = [
  { id: 'me-konto', label: 'Konto & Passwort', icon: 'account', keywords: 'passwort benutzername profil konto' },
  { id: 'me-bedienung', label: 'Bedienung', icon: 'ui', keywords: 'startseite bedienung ansicht' },
  { id: 'me-anmeldungen', label: 'Letzte Anmeldungen', icon: 'logins', keywords: 'anmeldungen login ip sitzung' },
];

const SERVER_TABS: { id: string; label: (k: Kind) => string; icon: PaletteIcon; keywords: string; when: (k: Kind, can: Can) => boolean }[] = [
  { id: 'overview', label: () => 'Übersicht', icon: 'overview', keywords: 'dashboard status', when: () => true },
  { id: 'console', label: () => 'Konsole', icon: 'console', keywords: 'befehl command rcon', when: (_k, c) => c('can_view_console') },
  { id: 'logs', label: () => 'Logs', icon: 'logs', keywords: 'protokoll latest fehler crash', when: (_k, c) => c('can_view_console') },
  { id: 'files', label: () => 'Dateien', icon: 'files', keywords: 'dateimanager upload ordner', when: (_k, c) => c('can_manage_files') },
  { id: 'software', label: (k) => (k === 'steam' ? 'Spiel & Mods' : 'Software'), icon: 'software', keywords: 'version loader paper fabric forge mods', when: () => true },
  { id: 'modpacks', label: () => 'Modpacks', icon: 'modpacks', keywords: 'modpack curseforge modrinth', when: (k) => k === 'java' },
  { id: 'optimize', label: () => 'Optimieren', icon: 'optimize', keywords: 'performance jvm lag', when: (k) => k === 'java' },
  { id: 'players', label: () => 'Spieler', icon: 'players', keywords: 'ban kick op online', when: (k) => k !== 'steam' },
  { id: 'map', label: () => 'Karte', icon: 'map', keywords: 'map welt', when: (k) => k === 'java' },
  { id: 'stats', label: () => 'Statistik', icon: 'stats', keywords: 'spielzeit', when: (k, c) => k !== 'steam' && c('can_view_player_stats') },
  { id: 'announcements', label: () => 'Ankündigungen', icon: 'announcements', keywords: 'nachricht broadcast zeitplan', when: (_k, c) => c('can_manage_schedules') || c('can_manage_backups') },
  { id: 'backups', label: () => 'Backups', icon: 'backups', keywords: 'sicherung wiederherstellen restore cloud', when: (_k, c) => c('can_manage_backups') },
  { id: 'settings', label: () => 'Einstellungen', icon: 'settings', keywords: 'konfiguration', when: () => true },
];

const SERVER_SECTIONS: { tab: string; tabLabel: string; anchor: string; label: (k: Kind) => string; icon: PaletteIcon; keywords: string; when: (k: Kind, can: Can, type?: string) => boolean }[] = [
  { tab: 'settings', tabLabel: 'Einstellungen', anchor: 'set-allgemein', label: () => 'Name, Port & Adresse', icon: 'general', keywords: 'name port adresse domain ip umbenennen', when: () => true },
  { tab: 'settings', tabLabel: 'Einstellungen', anchor: 'set-leistung', label: () => 'Arbeitsspeicher (RAM)', icon: 'ram', keywords: 'ram speicher leistung memory', when: () => true },
  { tab: 'settings', tabLabel: 'Einstellungen', anchor: 'set-spiel', label: (k) => (k === 'steam' ? 'Spiel-Einstellungen' : 'Spielwelt-Regeln'), icon: 'game', keywords: 'schwierigkeit whitelist pvp spieler motd spielmodus hardcore passwort karte properties', when: () => true },
  { tab: 'settings', tabLabel: 'Einstellungen', anchor: 'set-start', label: () => 'Start & Java', icon: 'java', keywords: 'java jar startskript launch', when: (k) => k === 'java' },
  { tab: 'settings', tabLabel: 'Einstellungen', anchor: 'set-updates', label: () => 'Automatische Updates', icon: 'updates', keywords: 'auto update aktualisieren', when: () => true },
  { tab: 'settings', tabLabel: 'Einstellungen', anchor: 'set-gefahr', label: () => 'Gefahrenzone (archivieren, löschen)', icon: 'danger', keywords: 'loschen archivieren entfernen', when: (_k, c) => c('can_delete_servers') },
  { tab: 'settings', tabLabel: 'Einstellungen', anchor: 'set-regeln', label: () => 'Spielregeln (XP, Zähmen, Zucht …)', icon: 'optimize', keywords: 'xp erfahrung zahmen ernte ausdauer hunger durst schaden zucht eier baby multiplikator raten pve', when: (_k, _c, type) => type === 'steam:376030' },
  { tab: 'announcements', tabLabel: 'Ankündigungen', anchor: 'ann-wartung', label: () => 'Wartungsmodus – nur dieser Server', icon: 'maintenance', keywords: 'wartung maintenance sperren', when: (_k, c) => c('can_manage_schedules') || c('can_manage_backups') },
  { tab: 'announcements', tabLabel: 'Ankündigungen', anchor: 'ann-neustarts', label: () => 'Geplante Neustarts', icon: 'updates', keywords: 'neustart restart zeitplan uhrzeit', when: (_k, c) => c('can_manage_schedules') || c('can_manage_backups') },
];

/** Alle Einträge, die dieser Benutzer sehen darf. */
export function buildPaletteItems(servers: PaletteServer[], can: Can): PaletteItem[] {
  const items: PaletteItem[] = !can('can_create_servers') ? [] : [
    { id: 'new', kind: 'action', label: 'Neuen Server erstellen', sub: 'Aktion', keywords: 'erstellen anlegen neu', icon: 'plus', action: 'create-server' },
  ];
  for (const s of servers) {
    items.push({
      id: `srv-${s.id}`, kind: 'server', label: s.name,
      sub: `${s.type ?? ''}${s.version ? ' ' + s.version : ''} · ${s.status === 'online' ? 'läuft' : 'gestoppt'}`,
      keywords: s.type, icon: 'server', to: `/server/${s.id}/overview`, serverId: s.id, serverName: s.name,
    });
  }
  for (const p of PAGES) if (p.allowed(can)) items.push({ id: `page-${p.p}`, kind: 'page', label: p.label, sub: 'Seite', keywords: p.keywords, icon: p.icon, to: `/${p.p}` });
  for (const s of SYSTEM_SECTIONS) if (s.allowed(can)) items.push({ id: s.id, kind: 'section', label: s.label, sub: 'System', keywords: s.keywords, icon: s.icon, to: '/system', anchor: s.id });
  if (can('can_manage_system') || can('can_root_reboot')) {
    items.push({ id: 'sysmon', kind: 'section', label: 'Systemmonitor', sub: 'System', keywords: 'cpu prozesse auslastung ressourcen monitor', icon: 'monitor', action: 'system-monitor' });
  }
  for (const s of ME_SECTIONS) items.push({ id: s.id, kind: 'section', label: s.label, sub: 'Einstellungen', keywords: s.keywords, icon: s.icon, to: '/settings', anchor: s.id });
  for (const s of servers) {
    const k = kindOf(s.type);
    for (const t of SERVER_TABS) {
      if (!t.when(k, can)) continue;
      items.push({ id: `tab-${s.id}-${t.id}`, kind: 'server-tab', label: t.label(k), sub: s.name, keywords: t.keywords, icon: t.icon, to: `/server/${s.id}/${t.id}`, serverId: s.id, serverName: s.name });
    }
    for (const sec of SERVER_SECTIONS) {
      if (!sec.when(k, can, s.type)) continue;
      items.push({
        id: `sec-${s.id}-${sec.anchor}`, kind: 'server-section', label: sec.label(k), sub: `${s.name} · ${sec.tabLabel}`,
        keywords: sec.keywords, icon: sec.icon, to: `/server/${s.id}/${sec.tab}`, anchor: sec.anchor, serverId: s.id, serverName: s.name,
      });
    }
  }
  return items;
}

/** Suchtext in Wörter: an Leerzeichen UND Satzzeichen trennen („(Jonas Mods)" → jonas, mods). */
const tokenize = (query: string) => normalize(query).split(/[^a-z0-9]+/).filter(Boolean);

/** Kurze Suchwörter müssen ein Wortanfang sein, ab 4 Zeichen reicht „kommt im Wort vor" (deutsche Komposita). */
const hits = (token: string, ws: string[]) => ws.some((w) => (token.length >= 4 ? w.includes(token) : w.startsWith(token)));

/** Trifft das Suchwort den Servernamen? Wortanfang („ete" → Eternal, „2" → 2) oder zusammengeschrieben („atm10"). */
const matchesServer = (t: string, serverWords: string[], serverCompact: string) =>
  serverWords.some((w) => w.startsWith(t)) || (t.length >= 2 && serverCompact.includes(t));

const isScoped = (item: PaletteItem) => item.kind === 'server-tab' || item.kind === 'server-section';

/**
 * Jedes Suchwort muss passen. Server-Unterpunkte erscheinen nur, wenn Suchwörter den
 * Servernamen treffen (oder man gerade in diesem Server ist) UND ein weiteres Wort den
 * Unterpunkt – sonst würde „ram" jeden Server einzeln auflisten.
 */
export function searchPalette(items: PaletteItem[], query: string, opts: { activeServerId?: string | null; limit?: number } = {}): PaletteItem[] {
  const { activeServerId = null, limit = 40 } = opts;
  const tokens = tokenize(query);
  if (!tokens.length) return items.filter((i) => i.kind === 'action' || i.kind === 'server' || i.kind === 'page');
  const queryCompact = tokens.join('');

  const scored: { item: PaletteItem; score: number; index: number }[] = [];
  items.forEach((item, index) => {
    const scoped = isScoped(item);
    const labelWords = words(item.label);
    const hayWords = scoped ? [...labelWords, ...words(item.keywords || '')] : [...labelWords, ...words(item.sub || ''), ...words(item.keywords || '')];
    const labelCompact = compact(item.label);
    const serverWords = scoped ? words(item.serverName || '') : [];
    const serverCompact = scoped ? compact(item.serverName || '') : '';
    const active = scoped && !!activeServerId && item.serverId === activeServerId;
    const serverTokens: string[] = [];
    let sectionTokens = 0;
    let score = 0;

    for (const t of tokens) {
      const inServer = scoped && matchesServer(t, serverWords, serverCompact);
      const labelHit = hits(t, labelWords) || (t.length >= 3 && labelCompact.includes(t));
      const hayHit = labelHit || hits(t, hayWords);
      if (!inServer && !hayHit) return;
      if (inServer) { serverTokens.push(t); score += 2; }
      // Ein Wort, das schon den Servernamen trifft, zählt nur im aktuellen Server auch als Stichwort.
      if (hayHit && (!inServer || active)) {
        sectionTokens++;
        score += labelHit ? (labelWords.some((w) => w.startsWith(t)) ? 4 : 3) : 1;
      }
    }

    if (scoped) {
      if (!sectionTokens) return;
      if (!serverTokens.length && !active) return;
      if (!serverTokens.length && active) score += 2;
      if (serverTokens.length && serverTokens.join('') === serverCompact) score += 3;
      if (item.kind === 'server-section') score += 1;
    }
    if (item.kind === 'server') score += 2 + (queryCompact === labelCompact ? 3 : 0);
    scored.push({ item, score, index });
  });

  scored.sort((a, b) => b.score - a.score || a.index - b.index);
  return scored.slice(0, limit).map((s) => s.item);
}

/**
 * Tab-Vervollständigung wie bei einer Suchmaschine, passend zum gewählten Eintrag:
 * erst den Servernamen („mc ete ram" → „MC Eternal 2 (Jonas Mods) ram"), dann das
 * angefangene Stichwort („… ra" → „… RAM", „wart" → „Wartungsmodus").
 */
export function completeQuery(query: string, item: PaletteItem | undefined, activeServerId: string | null = null): string | null {
  if (!item || !query.trim()) return null;
  const parts = query.trim().split(/\s+/);
  const serverName = item.kind === 'server' ? item.label : isScoped(item) ? item.serverName || '' : '';

  if (serverName) {
    const serverWords = words(serverName);
    const serverCompact = compact(serverName);
    const isServerPart = (p: string) => {
      const t = compact(p);
      return !!t && matchesServer(t, serverWords, serverCompact);
    };
    const nameComplete = tokenize(query).join('').includes(serverCompact);
    const viaActiveServer = isScoped(item) && item.serverId === activeServerId && !parts.some(isServerPart);
    if (!nameComplete && !viaActiveServer) {
      const rest = item.kind === 'server' ? [] : parts.filter((p) => !isServerPart(p));
      return rest.length ? `${serverName} ${rest.join(' ')}` : `${serverName} `;
    }
  }

  // Angefangenes letztes Wort ergänzen.
  if (/\s$/.test(query)) return null;
  const last = compact(parts[parts.length - 1] || '');
  if (!last) return null;
  const candidates = [...item.label.split(/[^\p{L}\p{N}]+/u), ...(item.keywords || '').split(/\s+/)].filter(Boolean);
  const word = candidates.find((w) => compact(w).startsWith(last) && compact(w) !== last);
  return word ? [...parts.slice(0, -1), word].join(' ') : null;
}
